import type { ObjectId } from 'mongodb';

import type { AccountMilestoneStatus } from '../AccountMilestones';
import type { Meta } from '../Entities';
import type { PayoutHold, PayoutHoldHistory } from '../OrganizationSettings';
import type { ProgramType } from '../ProgramTypes';
import type { SystemAssetWithTypeAndWarranty, SystemDesignHardwareLookup } from '../SystemDesigns';

export enum PayeeType {
  organization = 'organization',
  vendor = 'vendor',
}

export interface PayeePlanHistoryEntry {
  date: Date;
  planId: string;
  user: { id: string; name: string };
}

export interface PayeeDocument {
  id?: string;
  externalId: string; // org alias
  type: PayeeType;
  name: string;
  planId?: string;
  planName?: string;
  planHistory?: Array<PayeePlanHistoryEntry>;
  payoutHold?: PayoutHold;
  payoutHoldHistory?: PayoutHoldHistory;
  metadata?: Record<string, any>;
  meta?: Meta;
}

export enum PayeeProjectRole {
  primary = 'primary',
  materials = 'materials',
}

export enum MaterialsInvoiceStatus {
  pending = 'pending',
  missing = 'missing', // project has reached a milestone but materials invoice is missing, so we can't pay
  received = 'received',
}

export interface ProjectDocument {
  id?: string;
  /** LR accountId */
  externalId: string;
  name: string;
  totalCost: number;
  projectOwnerExternalId: string;
  metadata?: Record<string, any>;
  meta?: Meta;
  programType: ProgramType;
  planId?: string;
  planName?: string;
  projectDetails?: {
    systemAssets: SystemAssetWithTypeAndWarranty[];
    projectHardwareLookup?: SystemDesignHardwareLookup[];
    systemSizeKw?: number;
    propertyType?: string;
  };
  attributes?: Record<string, any>;
  payees: Array<{ id: ObjectId; role: PayeeProjectRole; name: string }>;
  materials?: { invoiceStatus?: MaterialsInvoiceStatus };
}

export interface InvoiceNeededInfo {
  accountId: string;
  name: string;
  materialsProjectId?: string;
  primaryPayeeName?: string;
  installMilestoneStatus?: AccountMilestoneStatus;
  activationMilestoneStatus?: AccountMilestoneStatus;
}
